import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../api/apiClient";
import { useT } from "../../i18n/LanguageProvider";
import type {
  CustomerOrder,
  CustomerOrderStatus,
  ListCustomerOrdersQuery,
  UserListEntry,
} from "../../api/types";
import { EmptyState } from "../ui/EmptyState";
import { Select } from "../ui/Select";
import { SegmentedControl } from "../ui/SegmentedControl";
import { OrderDetailModal } from "./OrderDetailModal";
import {
  fulfillmentLabel,
  orderDayDate,
  orderDayKey,
  orderStatusClass,
  orderStatusLabel,
} from "./orderUi";

const PAGE_SIZE = 50;

type RangePreset = "7d" | "30d" | "month" | "custom";
type FulfillmentChip = "ALL" | "DELIVERY" | "PICKUP";

const STATUS_OPTIONS: Array<{ value: CustomerOrderStatus; label: string }> = [
  { value: "NEW", label: "New" },
  { value: "CONFIRMED", label: "Confirmed" },
  { value: "IN_PREPARATION", label: "In preparation" },
  { value: "READY", label: "Ready" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
];

function presetRange(preset: RangePreset): { from: string; to: string } | null {
  const today = new Date();
  const key = (d: Date) => orderDayKey(d);
  if (preset === "7d") {
    const from = new Date(today);
    from.setDate(today.getDate() - 6);
    return { from: key(from), to: key(today) };
  }
  if (preset === "30d") {
    const from = new Date(today);
    from.setDate(today.getDate() - 29);
    return { from: key(from), to: key(today) };
  }
  if (preset === "month") {
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { from: key(from), to: key(to) };
  }
  return null;
}

/**
 * G-14: the filterable order history — answers "all deliveries over 100 € in
 * May", "everything Maria prepared last week", "cancelled orders this month".
 * Newest first, load-more pagination; a row opens the existing day detail.
 */
export function OrdersHistory({ locationId }: { locationId: string }) {
  const { t } = useT();
  const [preset, setPreset] = useState<RangePreset>("month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [fulfillmentChip, setFulfillmentChip] = useState<FulfillmentChip>("ALL");
  const [status, setStatus] = useState<"" | CustomerOrderStatus>("");
  const [preparedByUserId, setPreparedByUserId] = useState("");
  const [courierUserId, setCourierUserId] = useState("");
  const [minTotal, setMinTotal] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [rows, setRows] = useState<CustomerOrder[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Person filters need the staff list; managers/owners only — for anyone
  // else the two selects simply stay hidden.
  const [people, setPeople] = useState<UserListEntry[]>([]);
  useEffect(() => {
    let stale = false;
    void api
      .listUsers()
      .then((list) => {
        if (!stale) setPeople(list);
      })
      .catch(() => {
        if (!stale) setPeople([]);
      });
    return () => {
      stale = true;
    };
  }, []);

  const preparers = useMemo(
    () =>
      people.filter(
        (u) =>
          (u.role === "OWNER" || u.role === "MANAGER" || u.role === "FLORIST") &&
          (u.isActive || u.id === preparedByUserId),
      ),
    [people, preparedByUserId],
  );
  const couriers = useMemo(
    () =>
      people.filter(
        (u) => u.role === "COURIER" && (u.isActive || u.id === courierUserId),
      ),
    [people, courierUserId],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  const query = useMemo<ListCustomerOrdersQuery>(() => {
    const range =
      preset === "custom"
        ? { from: customFrom || undefined, to: customTo || undefined }
        : (presetRange(preset) ?? {});
    const minTotalEur = Number(minTotal);
    return {
      locationId,
      ...range,
      status: status || undefined,
      fulfillmentKind: fulfillmentChip === "ALL" ? undefined : fulfillmentChip,
      preparedByUserId: preparedByUserId || undefined,
      courierUserId: courierUserId || undefined,
      minTotalEur:
        minTotal.trim() !== "" && Number.isFinite(minTotalEur) && minTotalEur >= 0
          ? minTotalEur
          : undefined,
      search: debouncedSearch.trim() || undefined,
      take: PAGE_SIZE,
    };
  }, [
    locationId,
    preset,
    customFrom,
    customTo,
    status,
    fulfillmentChip,
    preparedByUserId,
    courierUserId,
    minTotal,
    debouncedSearch,
  ]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await api.listCustomerOrders({ ...query, skip: 0 });
      setRows(list);
      setHasMore(list.length === PAGE_SIZE);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not load the history.");
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    setError(null);
    try {
      const list = await api.listCustomerOrders({ ...query, skip: rows.length });
      setRows((cur) => [...cur, ...list]);
      setHasMore(list.length === PAGE_SIZE);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not load more orders.");
    } finally {
      setLoadingMore(false);
    }
  }, [query, rows.length]);

  // G-16 decision 2: a history row opens THAT order directly (the old flow
  // fetched the whole day and opened the day modal — the calendar keeps that
  // behaviour; history rows go straight to the one canonical detail surface).
  const [activeOrder, setActiveOrder] = useState<CustomerOrder | null>(null);

  return (
    <div className="orders-history">
      <div className="card orders-history-filters">
        <div className="orders-history-filter-row">
          <div className="field">
            <label htmlFor="orders-history-range">Period</label>
            <Select
              id="orders-history-range"
              value={preset}
              onChange={(e) => setPreset(e.target.value as RangePreset)}
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="month">This month</option>
              <option value="custom">Custom range</option>
            </Select>
          </div>
          {preset === "custom" ? (
            <>
              <div className="field">
                <label htmlFor="orders-history-from">From</label>
                <input
                  id="orders-history-from"
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="orders-history-to">To</label>
                <input
                  id="orders-history-to"
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                />
              </div>
            </>
          ) : null}
          <div className="field">
            <label id="orders-history-fulfillment-label">Fulfillment</label>
            <SegmentedControl<FulfillmentChip>
              ariaLabel="Fulfillment"
              value={fulfillmentChip}
              onChange={setFulfillmentChip}
              options={[
                { value: "ALL", label: "All" },
                { value: "DELIVERY", label: "Delivery" },
                { value: "PICKUP", label: "Pickup" },
              ]}
            />
          </div>
          <div className="field">
            <label htmlFor="orders-history-status">Status</label>
            <Select
              id="orders-history-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as "" | CustomerOrderStatus)}
            >
              <option value="">Any status</option>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="orders-history-filter-row">
          {preparers.length > 0 ? (
            <div className="field">
              <label htmlFor="orders-history-prepared-by">Prepared by</label>
              <Select
                id="orders-history-prepared-by"
                value={preparedByUserId}
                onChange={(e) => setPreparedByUserId(e.target.value)}
              >
                <option value="">Anyone</option>
                {preparers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.fullName}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
          {couriers.length > 0 ? (
            <div className="field">
              <label htmlFor="orders-history-courier">Courier</label>
              <Select
                id="orders-history-courier"
                value={courierUserId}
                onChange={(e) => setCourierUserId(e.target.value)}
              >
                <option value="">Any courier</option>
                {couriers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.fullName}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
          <div className="field orders-history-min-total">
            <label htmlFor="orders-history-min-total">Above € </label>
            <input
              id="orders-history-min-total"
              type="number"
              min={0}
              step="0.01"
              placeholder="e.g. 100"
              value={minTotal}
              onChange={(e) => setMinTotal(e.target.value)}
            />
          </div>
          <div className="field orders-history-search">
            <label htmlFor="orders-history-search">Search</label>
            <input
              id="orders-history-search"
              type="search"
              placeholder="Customer or order code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      <div className="card">
        {loading ? (
          <p className="muted">Loading orders…</p>
        ) : rows.length === 0 ? (
          <EmptyState
            title="No orders match these filters"
            message="Widen the period or clear a filter to see more."
          />
        ) : (
          <>
            <div className="table-scroll">
              <table className="orders-history-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Code</th>
                    <th>Customer</th>
                    <th>Fulfillment</th>
                    <th>Status</th>
                    <th>Total</th>
                    <th>Prepared by</th>
                    <th>Courier</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((order) => (
                    <tr
                      key={order.id}
                      className="orders-history-row"
                      tabIndex={0}
                      onClick={() => setActiveOrder(order)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setActiveOrder(order);
                        }
                      }}
                      aria-label={`Open ${order.code} — ${order.customerName}`}
                    >
                      <td>
                        {orderDayDate(order).toLocaleDateString(undefined, {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </td>
                      <td className="orders-history-code">{order.code}</td>
                      <td>{order.customerName}</td>
                      <td>{fulfillmentLabel(t, order.fulfillment)}</td>
                      <td>
                        <span className={orderStatusClass(order.status)}>
                          {orderStatusLabel(t, order.status)}
                        </span>
                        {/* OD-32: reality diverged from the request. */}
                        {order.divergedFromRequest ? (
                          <span className="badge warning">differs</span>
                        ) : null}
                      </td>
                      <td className="orders-history-total">
                        {order.totalEur ? `${order.totalEur} €` : "—"}
                      </td>
                      <td>{order.preparedBy?.fullName ?? "—"}</td>
                      <td>{order.deliveryJob?.courier?.fullName ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="orders-history-foot">
              <span className="muted">
                {rows.length === 1 ? "1 order" : `${rows.length} orders`}
                {hasMore ? " so far" : ""}
              </span>
              {hasMore ? (
                <button
                  type="button"
                  className="button tinted"
                  onClick={() => void loadMore()}
                  disabled={loadingMore}
                >
                  {loadingMore ? "Loading…" : "Load more"}
                </button>
              ) : null}
            </div>
          </>
        )}
      </div>

      {activeOrder ? (
        <OrderDetailModal
          order={activeOrder}
          onClose={() => setActiveOrder(null)}
          onChanged={async () => {
            await refresh();
            // Keep the open modal's order fresh after an action.
            const updated = await api.getCustomerOrder(activeOrder.id).catch(() => null);
            if (updated) setActiveOrder(updated);
          }}
        />
      ) : null}
    </div>
  );
}
